// src/components/settings/SettingsPanel.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Settings, 
  Mic, 
  Camera, 
  Volume2, 
  Monitor,
  Smartphone,
  Tablet
} from 'lucide-react';

const SettingsContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: #2a2a2a;
  color: white;
  z-index: 1000;
  transform: ${props => props.open ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.3);
`;

const SettingsHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #444;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SettingsContent = styled.div`
  padding: 20px;
  height: calc(100vh - 80px);
  overflow-y: auto;
`;

const SettingSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #4CAF50;
`;

const SettingItem = styled.div`
  margin-bottom: 20px;
`;

const SettingLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  background: #333;
  border: 1px solid #444;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
  
  option {
    background: #333;
  }
`;

const VolumeSlider = styled.input`
  width: 100%;
  margin: 10px 0;
  -webkit-appearance: none;
  height: 6px;
  border-radius: 3px;
  background: #444;
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #4CAF50;
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #4CAF50;
    cursor: pointer;
    border: none;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #4CAF50;
`;

const TestButton = styled.button`
  padding: 8px 16px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #45a049;
  }
`;

const DevicePreview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  margin: 15px 0;
`;

const DeviceCard = styled.div`
  background: #333;
  border: 2px solid ${props => props.active ? '#4CAF50' : '#444'};
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #4CAF50;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 24px;
`;

const SettingsPanel = ({ open, onClose }) => {
  const [settings, setSettings] = useState({
    microphone: 'Logitech USB Headset H3',
    speaker: 'Logitech USB Headset H3',
    camera: 'Integrated Camera',
    micVolume: 75,
    speakerVolume: 54,
    mirrorMode: true,
    aiDenoise: false,
    classroomMode: 'standard', // standard, large-screen
    cameraArea: 'show', // show, hide
    deviceType: 'desktop' // desktop, tablet, mobile
  });

  const [availableDevices, setAvailableDevices] = useState({
    microphones: [
      'Logitech USB Headset H3',
      'USB Audio',
      'Disable'
    ],
    speakers: [
      'Logitech USB Headset H3',
      'Disable'
    ],
    cameras: [
      'Integrated Camera',
      'OBS-Camera',
      'OBS-Camera2',
      'OBS-Camera3',
      'OBS-Camera4',
      'Disable'
    ]
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSpeakerTest = () => {
    // Play test sound
    console.log('Testing speaker...');
  };

  const handleDeviceTypeChange = (deviceType) => {
    setSettings(prev => ({
      ...prev,
      deviceType
    }));
  };

  const handleFinish = () => {
    // Save settings
    localStorage.setItem('classroomSettings', JSON.stringify(settings));
    onClose();
  };

  if (!open) return null;

  return (
    <SettingsContainer open={open}>
      <CloseButton onClick={onClose}>×</CloseButton>
      
      <SettingsHeader>
        <Settings size={20} />
        <span>Settings</span>
      </SettingsHeader>

      <SettingsContent>
        <SettingSection>
          <SectionTitle>Audio Settings</SectionTitle>
          
          <SettingItem>
            <SettingLabel>
              <Mic size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Microphone
            </SettingLabel>
            <Select
              value={settings.microphone}
              onChange={(e) => handleSettingChange('microphone', e.target.value)}
            >
              {availableDevices.microphones.map(mic => (
                <option key={mic} value={mic}>{mic}</option>
              ))}
            </Select>
            <VolumeSlider
              type="range"
              min="0"
              max="100"
              value={settings.micVolume}
              onChange={(e) => handleSettingChange('micVolume', e.target.value)}
            />
            <div style={{ fontSize: '12px', opacity: 0.7 }}>
              Volume: {settings.micVolume}%
            </div>
          </SettingItem>

          <SettingItem>
            <SettingLabel>
              <Volume2 size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Speaker
            </SettingLabel>
            <Select
              value={settings.speaker}
              onChange={(e) => handleSettingChange('speaker', e.target.value)}
            >
              {availableDevices.speakers.map(speaker => (
                <option key={speaker} value={speaker}>{speaker}</option>
              ))}
            </Select>
            <VolumeSlider
              type="range"
              min="0"
              max="100"
              value={settings.speakerVolume}
              onChange={(e) => handleSettingChange('speakerVolume', e.target.value)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', opacity: 0.7 }}>
                Volume: {settings.speakerVolume}%
              </span>
              <TestButton onClick={handleSpeakerTest}>
                Speaker Test
              </TestButton>
            </div>
          </SettingItem>

          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              checked={settings.aiDenoise}
              onChange={(e) => handleSettingChange('aiDenoise', e.target.checked)}
            />
            <label>AI Denoise (Beta)</label>
          </CheckboxContainer>
        </SettingSection>

        <SettingSection>
          <SectionTitle>Video Settings</SectionTitle>
          
          <SettingItem>
            <SettingLabel>
              <Camera size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Local Camera
            </SettingLabel>
            <Select
              value={settings.camera}
              onChange={(e) => handleSettingChange('camera', e.target.value)}
            >
              {availableDevices.cameras.map(camera => (
                <option key={camera} value={camera}>{camera}</option>
              ))}
            </Select>
          </SettingItem>

          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              checked={settings.mirrorMode}
              onChange={(e) => handleSettingChange('mirrorMode', e.target.checked)}
            />
            <label>Mirror Mode</label>
          </CheckboxContainer>
        </SettingSection>

        <SettingSection>
          <SectionTitle>Display Settings</SectionTitle>
          
          <SettingItem>
            <SettingLabel>Classroom Mode</SettingLabel>
            <CheckboxContainer>
              <Checkbox
                type="radio"
                name="classroomMode"
                checked={settings.classroomMode === 'standard'}
                onChange={() => handleSettingChange('classroomMode', 'standard')}
              />
              <label>Standard</label>
            </CheckboxContainer>
            <CheckboxContainer>
              <Checkbox
                type="radio"
                name="classroomMode"
                checked={settings.classroomMode === 'large-screen'}
                onChange={() => handleSettingChange('classroomMode', 'large-screen')}
              />
              <label>Large-screen</label>
            </CheckboxContainer>
          </SettingItem>

          <SettingItem>
            <SettingLabel>Camera Area</SettingLabel>
            <CheckboxContainer>
              <Checkbox
                type="radio"
                name="cameraArea"
                checked={settings.cameraArea === 'show'}
                onChange={() => handleSettingChange('cameraArea', 'show')}
              />
              <label>Show</label>
            </CheckboxContainer>
            <CheckboxContainer>
              <Checkbox
                type="radio"
                name="cameraArea"
                checked={settings.cameraArea === 'hide'}
                onChange={() => handleSettingChange('cameraArea', 'hide')}
              />
              <label>Hide</label>
            </CheckboxContainer>
          </SettingItem>
        </SettingSection>

        <SettingSection>
          <SectionTitle>Device Type</SectionTitle>
          <DevicePreview>
            <DeviceCard 
              active={settings.deviceType === 'desktop'}
              onClick={() => handleDeviceTypeChange('desktop')}
            >
              <Monitor size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '12px' }}>Desktop</div>
            </DeviceCard>
            <DeviceCard 
              active={settings.deviceType === 'tablet'}
              onClick={() => handleDeviceTypeChange('tablet')}
            >
              <Tablet size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '12px' }}>Tablet</div>
            </DeviceCard>
            <DeviceCard 
              active={settings.deviceType === 'mobile'}
              onClick={() => handleDeviceTypeChange('mobile')}
            >
              <Smartphone size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '12px' }}>Mobile</div>
            </DeviceCard>
          </DevicePreview>
        </SettingSection>

        <div style={{ padding: '20px 0', borderTop: '1px solid #444' }}>
          <TestButton 
            onClick={handleFinish}
            style={{ width: '100%', padding: '12px' }}
          >
            Finish
          </TestButton>
        </div>
      </SettingsContent>
    </SettingsContainer>
  );
};

export default SettingsPanel;